import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { PHONE_QUERY, useMediaQuery } from "@bastion/ui";
import {
  INTENSITY_PALETTE,
  formatValue,
  intensityColor,
  type ValueFormat,
} from "../lib";
import type { DashboardWidget, WidgetDataRow } from "../types";
import styles from "./WidgetRenderer.module.css";

/** Pixel constants — kept here so the SVG geometry stays in one place. */
const CELL_SIZE = 11;
const CELL_GAP = 2;
const STEP = CELL_SIZE + CELL_GAP; // 13
const ROWS = 7;
const LEFT_PAD = 24; // room for the M/W/F day labels
const TOP_PAD = 18; // room for the month labels

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

type CalendarHeatmapWidget = Extract<DashboardWidget, { type: "calendar_heatmap" }>;

export interface CalendarHeatmapProps {
  widget: CalendarHeatmapWidget;
  rows: WidgetDataRow[];
}

interface Cell {
  /** YYYY-MM-DD date key (UTC). */
  key: string;
  /**
   * Date object whose UTC components (year/month/day) represent the
   * cell's calendar day. The local-time components are meaningless —
   * always read via `getUTC*`.
   */
  date: Date;
  /** Column index (0 = oldest week shown). */
  column: number;
  /** Row index within the week (0 = `start_day`). */
  row: number;
  /** Numeric value for the day, or null if no row was returned. */
  value: number | null;
}

interface MonthLabel {
  column: number;
  label: string;
}

/**
 * Render a GitHub-contributions-style calendar of daily activity.
 *
 * The renderer is intentionally a small custom SVG — Recharts has no
 * calendar heatmap primitive and the geometry (fixed 11px cells, gap,
 * day-of-week rows, month labels) is so prescriptive that hand-rolling
 * it is cheaper than wrangling a generic chart library.
 *
 * Empty days deliberately render with `INTENSITY_PALETTE[0]` (the
 * lightest tint) so the empty state is the same color as a "0 events"
 * day — they are semantically identical and forcing a separate "no
 * data" color would just add visual noise.
 */
export function CalendarHeatmap({ widget, rows }: CalendarHeatmapProps) {
  const weeks = widget.config.weeks ?? 26;
  const startDay = widget.config.start_day ?? "monday";
  const valueFormat: ValueFormat = widget.config.value_format ?? "compact";
  const dateColumn = widget.config.date;
  const valueColumn = widget.config.value;

  const { cells, monthLabels, maxValue } = useMemo(
    () => buildCalendar({ rows, weeks, startDay, dateColumn, valueColumn }),
    [rows, weeks, startDay, dateColumn, valueColumn],
  );

  // Hovered cell + the page-relative pixel coordinates of the rect that
  // raised the pointer event. We can't position the tooltip from
  // `LEFT_PAD + column * STEP` directly because the SVG uses
  // `width="100%"` with a `viewBox`, so SVG coordinates get scaled to
  // fit the wrap div. Reading the rect's actual bounding box lets the
  // tooltip stay attached to the cell at every container width. The
  // left edge includes the scroll of the wrap, because the tooltip
  // scrolls with the wrap content.
  const [hovered, setHovered] = useState<{
    cell: Cell;
    leftPx: number;
    topPx: number;
  } | null>(null);

  const width = LEFT_PAD + weeks * STEP - CELL_GAP;
  const height = TOP_PAD + ROWS * STEP - CELL_GAP;

  // On a phone the SVG keeps its drawn width (--heatmap-w in
  // WidgetRenderer.module.css), so the 11px labels do not shrink, and
  // the wrap scrolls. The latest weeks show first.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const phone = useMediaQuery(PHONE_QUERY);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!phone || !wrap) return;
    wrap.scrollLeft = wrap.scrollWidth;
  }, [phone, width]);

  // Day-of-week labels: just M / W / F to keep the column tight.
  // The label-to-row mapping depends on which day starts the week.
  const dayLabelRows: Array<{ row: number; label: string }> =
    startDay === "monday"
      ? [
          { row: 0, label: "M" },
          { row: 2, label: "W" },
          { row: 4, label: "F" },
        ]
      : [
          { row: 1, label: "M" },
          { row: 3, label: "W" },
          { row: 5, label: "F" },
        ];

  return (
    <div className={styles.heatmapWrap} ref={wrapRef}>
      <svg
        className={styles.heatmapSvg}
        style={{ "--heatmap-w": `${width}px` } as CSSProperties}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="xMinYMid meet"
        role="img"
        aria-label="Calendar heatmap"
      >
        {monthLabels.map(({ column, label }) => (
          <text
            key={`${column}-${label}`}
            x={LEFT_PAD + column * STEP}
            y={TOP_PAD - 6}
            className={styles.heatmapMonthLabel}
          >
            {label}
          </text>
        ))}

        {dayLabelRows.map(({ row, label }) => (
          <text
            key={label}
            x={LEFT_PAD - 6}
            y={TOP_PAD + row * STEP + CELL_SIZE - 2}
            textAnchor="end"
            className={styles.heatmapDayLabel}
          >
            {label}
          </text>
        ))}

        {cells.map((cell) => {
          const normalized =
            cell.value === null || maxValue <= 0 ? 0 : cell.value / maxValue;
          const fill =
            cell.value === null
              ? INTENSITY_PALETTE[0]
              : intensityColor(normalized);
          const isHovered = hovered?.cell.key === cell.key;
          return (
            <rect
              key={cell.key}
              x={LEFT_PAD + cell.column * STEP}
              y={TOP_PAD + cell.row * STEP}
              width={CELL_SIZE}
              height={CELL_SIZE}
              rx={2}
              ry={2}
              fill={fill}
              className={styles.heatmapCell}
              data-testid={`calendar-heatmap-cell-${cell.key}`}
              data-active={isHovered ? "true" : undefined}
              onPointerEnter={(event) => {
                const target = event.currentTarget as SVGRectElement;
                const rect = target.getBoundingClientRect();
                const wrap = target.ownerSVGElement?.parentElement;
                if (!wrap) {
                  setHovered({ cell, leftPx: 0, topPx: 0 });
                  return;
                }
                const wrapRect = wrap.getBoundingClientRect();
                // Anchor on the cell's center horizontally and just above
                // its top edge vertically. CSS `transform: translateX(-50%)`
                // (in the .heatmapTooltip class) re-centers around `left`.
                setHovered({
                  cell,
                  leftPx: rect.left - wrapRect.left + wrap.scrollLeft + rect.width / 2,
                  topPx: rect.top - wrapRect.top - 6,
                });
              }}
              onPointerLeave={() =>
                setHovered((current) => (current?.cell.key === cell.key ? null : current))
              }
            >
              <title>
                {`${formatCellDate(cell.date)} — ${
                  cell.value === null ? "no data" : formatValue(cell.value, { format: valueFormat })
                }`}
              </title>
            </rect>
          );
        })}
      </svg>

      {hovered ? (
        <div
          className={styles.heatmapTooltip}
          style={{
            left: hovered.leftPx,
            top: hovered.topPx,
          }}
          role="tooltip"
        >
          <div className={styles.heatmapTooltipDate}>{formatCellDate(hovered.cell.date)}</div>
          <div className={styles.heatmapTooltipValue}>
            {hovered.cell.value === null
              ? "No data"
              : formatValue(hovered.cell.value, { format: valueFormat })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface BuildCalendarArgs {
  rows: WidgetDataRow[];
  weeks: number;
  startDay: "sunday" | "monday";
  dateColumn: string;
  valueColumn: string;
}

interface BuildCalendarResult {
  cells: Cell[];
  monthLabels: MonthLabel[];
  maxValue: number;
}

function buildCalendar({
  rows,
  weeks,
  startDay,
  dateColumn,
  valueColumn,
}: BuildCalendarArgs): BuildCalendarResult {
  const valuesByKey = new Map<string, number>();
  for (const row of rows) {
    const rawDate = row[dateColumn];
    const rawValue = row[valueColumn];
    const key = normalizeDateKey(rawDate);
    if (key === null) continue;
    const numeric = toFiniteNumber(rawValue);
    if (numeric === null) continue;
    // If the SQL accidentally returns multiple rows per day, take the
    // sum so the renderer still shows the day's total intensity rather
    // than only whichever row PostgREST handed back last.
    valuesByKey.set(key, (valuesByKey.get(key) ?? 0) + numeric);
  }

  // Anchor on the user's local "today" but immediately freeze it as a
  // UTC calendar date so the rest of the grid math (iteration via
  // `setUTCDate`, key formatting via `getUTC*`) stays on a single
  // calendar. Using UTC throughout means a cell's `key` (used for
  // bucketing input rows) and the same key reused for hover lookup
  // can never drift across DST boundaries — `setDate` would otherwise
  // produce a Date whose local-tz Y/M/D differs from the UTC Y/M/D
  // when crossing a DST cutover.
  const today = todayUtcDate();
  const startDayIndex = startDay === "monday" ? 1 : 0; // 0 = Sunday, 1 = Monday
  const todayDow = today.getUTCDay();
  // Days remaining until the LAST cell of the current week:
  // For monday-start weeks, the last row is Sunday (dow = 0).
  // For sunday-start weeks, the last row is Saturday (dow = 6).
  const offsetToEndOfWeek = (startDayIndex + 6 - todayDow + 7) % 7;
  const endDate = addDays(today, offsetToEndOfWeek);
  const startDate = addDays(endDate, -(weeks * 7 - 1));

  const cells: Cell[] = [];
  let maxValue = 0;
  for (let i = 0; i < weeks * 7; i += 1) {
    const day = addDays(startDate, i);
    const key = formatDateKey(day);
    const column = Math.floor(i / 7);
    const row = i % 7;
    const cellValue = valuesByKey.get(key);
    const value = cellValue === undefined ? null : cellValue;
    if (value !== null && value > maxValue) maxValue = value;
    cells.push({ key, date: day, column, row, value });
  }

  // Month labels: emit a label for every column whose first day is in a
  // new month (and one for column 0 unconditionally).
  const monthLabels: MonthLabel[] = [];
  let lastMonth = -1;
  for (let column = 0; column < weeks; column += 1) {
    const firstDayOfColumn = cells[column * 7].date;
    const month = firstDayOfColumn.getUTCMonth();
    if (column === 0 || month !== lastMonth) {
      // Skip column 0 if its first day is past the 24th — the next
      // column's label would render too close, so prefer the next one.
      if (column === 0 && firstDayOfColumn.getUTCDate() > 24) {
        lastMonth = month;
        continue;
      }
      monthLabels.push({ column, label: MONTH_LABELS[month] });
      lastMonth = month;
    }
  }

  return { cells, monthLabels, maxValue };
}

function normalizeDateKey(value: unknown): string | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return formatDateKey(value);
  }
  if (typeof value === "string") {
    // Accept "YYYY-MM-DD" as-is; accept full ISO timestamps by truncating.
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return null;
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return formatDateKey(new Date(value));
  }
  return null;
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Today's wall-clock date in the user's locale, materialized as a Date
 * whose UTC components match those local Y/M/D values. We anchor on
 * local "today" so the heatmap matches what a user expects to see at a
 * glance, but freeze it as UTC midnight so subsequent iteration via
 * `setUTCDate` advances the calendar day exactly once per step (no DST
 * spring-forward / fall-back drift).
 */
function todayUtcDate(): Date {
  const now = new Date();
  return new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0),
  );
}

function addDays(date: Date, days: number): Date {
  const out = new Date(date);
  out.setUTCDate(out.getUTCDate() + days);
  return out;
}

function formatDateKey(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Render a cell's calendar day for tooltip display. We can't use the
 * shared `formatTimestamp` here because passing a YYYY-MM-DD string
 * into `new Date()` parses it as UTC midnight, then `toLocaleString`
 * shifts back to local time — which renders as the previous day in
 * negative-UTC-offset timezones (e.g. "Apr 28, 2026, 7:00 PM" for
 * what's semantically Apr 29). Forcing `timeZone: "UTC"` keeps the
 * displayed day identical to the bucketing key.
 */
function formatCellDate(date: Date, locale?: string): string {
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}
